import {getTypedEntries} from "@/core/util/getTypedEntries";
import splitWithTail from "@/core/util/splitWithTail";
import {urlParse} from "@/core/util/urlParse";
import {nextTick, toValue} from "vue";
import {createRouterMatcher, Router} from "vue-router";
import {RouteComponentMaybeStackable, TStackConfig} from "../types";
import {Stack} from './Stack';
import {StackItem} from "./StackItem";
import {StackManagerStub} from "./StackManagerStub";

export class StackManager {

    public stacks: Record<string, Stack> = {}
    public skipPopstate: boolean = false
    public router!: Router
    public pageScrollPosition: number = 0
    public pageUrl?: string

    public lastPushedRoutableStack?: Stack | null

    public routerPathChanged: boolean = false

    constructor() {
        if (!process.env.SERVER) {
            window.addEventListener('popstate', this.onHistoryBack.bind(this), false)
        }
    }

    getStackRouteByHash(hash: string) {

        if (hash && hash.match(/\#/)) {

            const [stackName, stackRoute] = splitWithTail(hash.replace('#', ''), '=', 1)

            if (stackName && stackRoute && this.getStack(stackName)) {

                const url = urlParse(stackRoute)

                if (url.path) {
                    const matcher = createRouterMatcher(
                        this.getStackableRoutes() as any,
                        {}
                    )
                    let res = matcher.resolve(
                        {
                            path: url.pathname,
                        },
                        {
                            path: '/',
                            name: undefined,
                            params: {},
                            matched: [],
                            meta: {},
                        }
                    )

                    if (res) {
                        res = {
                            ...res,
                            params: {
                                ...res.params,
                                ...url.query
                            }
                        }
                        return res
                    }
                }
            }
        }
    }

    getStackableRoutes() {
        const routes = this.router.getRoutes() as RouteComponentMaybeStackable[]
        return routes.filter(route => {
            return !!route.components?.default?._isStackable
        })
    }

    setVueRouter(router: Router) {
        this.router = router
    }

    onHistoryBack() {
        if (this.skipPopstate) {
            return;
        }
        const stack = this.lastPushedRoutableStack || this.getRoutableStack()
        if (stack) {
            if (stack.haveItems()) {
                stack.removeLast(true)
                nextTick(() => {
                    window.scrollTo({top: this.pageScrollPosition, behavior: 'instant'})
                })
            }
        }
    }

    initStack<TConfig extends TStackConfig = TStackConfig>(name: string, config?: TConfig, router?: Router) {
        this.stacks[name] = new Stack<TConfig>(this, name, config)
        if (router) {
            this.setVueRouter(router)
        }
    }

    getStack<TConfig extends TStackConfig = TStackConfig>(name: string = 'modal', config?: TConfig) {
        if (!this.stacks[name]) {
            this.initStack<TConfig>(name, config)
        }
        return this.stacks[name]
    }

    getStackByItemId<TConfig extends TStackConfig = TStackConfig>(id: string) {
        if (!id || typeof id !== 'string') {
            console.warn('StackManager.getStackByItemId: id is undefined or not a string', id);
            return this.getStack<TConfig>('modal') as Stack;
        }
        const [stackName] = id.split('-')
        return this.getStack<TConfig>(stackName) as Stack
    }

    getItemById(id: string, required: boolean = false) {
        const stack = this.getStackByItemId(id)
        return stack.getById(id, required)
    }

    onQueueChanged() {

    }

    getLocation() {
        // Возвращаем относительный путь вместо полного URL
        // Это критично для корректной работы Vue Router
        const path = window.location.pathname + window.location.search
        const hash = window.location.hash.replace('#', '')
        return {
            path: path || "/",
            hash: hash || ""
        }
    }

    onBeforePushItem(item: StackItem) {
        if (!item.stack.haveItems()) {
            this.pageScrollPosition = window.scrollY
            this.pageUrl = window.location.href
        }
    }

    onPushItem(item: StackItem) {
        if (item.isRoutable()) {
            this.lastPushedRoutableStack = item.stack
            const fullPath = item.route?.fullPath || item.route?.path || ''
            if (fullPath) {
                this.historyPush(null, item.stack.name + '=' + fullPath)
            } else {
                console.warn('[StackManager] Cannot push item to history: missing fullPath and path', item)
            }
        }
    }

    onRemoveItem(item: StackItem) {
        if (item.isRoutable()) {

            this.lastPushedRoutableStack = null

            document.dispatchEvent(new CustomEvent("router.watchStatusChange", {
                detail: {
                    status: false
                }
            }))

            this.skipPopstate = true

            if (!item.route.initialRoute) {
                console.log('!item.route.initialRoute === true')
                const hash = window.location.hash
                if (hash && hash !== '#') {
                    if (!this.routerPathChanged) {
                        console.log('back')
                        window.history.back()
                    } else {
                        console.log('historyPush(null, \'\', true)')
                        this.historyPush(null, '', true)
                    }
                } else {
                    console.log('no hash')
                }
            } else {
                console.log('!item.route.initialRoute === false')
            }

            setTimeout(() => {
                this.skipPopstate = false
                document.dispatchEvent(new CustomEvent("router.watchStatusChange", {
                    detail: {
                        status: true
                    }
                }))
            }, 50)
        }
    }

    historyPush(path: string | null | undefined, hash?: string, replace = false) {
        const {path: currentPath} = this.getLocation()
        let url = path || currentPath

        // Валидация: проверяем что URL не является строкой "undefined" или "null"
        if (!url || url === 'undefined' || url === 'null') {
            console.error('[StackManager] Invalid URL in historyPush:', {
                path,
                currentPath,
                hash
            })
            return;
        }

        // Валидация hash: не добавляем если это строка "undefined", "null" или пустая строка
        if (hash && hash !== 'undefined' && hash !== 'null' && hash !== '') {
            url += '#' + hash
        }

        const new_state = {
            current: currentPath
        }

        if (replace) {
            window.history.replaceState(new_state, '', url)
        } else {
            window.history.pushState(new_state, '', url)
        }
    }

    getRoutableStack() {
        return Object.values(this.stacks).find(stack => stack.config.routable)
    }

    haveOpenedItems() {
        for (const [stackName, stack] of getTypedEntries(this.stacks)) {
            for (const item of stack.queue) {
                if (toValue(item.visible) === true) {
                    return true
                }
            }
        }
    }

    removeAll() {
        for (const [stackName, stack] of getTypedEntries(this.stacks)) {
            stack.removeAll()
        }
    }
}

// Экспортируем stub на сервере, реальный StackManager на клиенте
export const stackManager = process.env.SERVER
    ? new StackManagerStub() as unknown as StackManager
    : new StackManager()



